import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
} from '@floating-ui/react';
import { AnimatePresence, motion } from 'motion/react';
import { type ReactNode, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import {
  useEditorContainer,
  useEditorFloatingContainer,
} from '../base-editor-context';

interface EditorCommandFloatingProps {
  readonly children: ReactNode;
  readonly show: boolean;
  readonly getReferenceClientRect: (() => DOMRect | null) | null;
}

/**
 * Floating UI wrapper for the editor slash command menu.
 * Uses virtual positioning based on cursor/selection position from TipTap.
 * Uses fixed positioning (viewport coordinates) to match TipTap's clientRect format.
 */
export function EditorCommandFloating({
  children,
  show,
  getReferenceClientRect,
}: EditorCommandFloatingProps) {
  const editorContainer = useEditorContainer();
  const floatingContainer = useEditorFloatingContainer();
  const [isReady, setIsReady] = useState(false);
  const [originPoint, setOriginPoint] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const { refs, floatingStyles, x, y } = useFloating({
    open: show,
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [offset(8), flip(), shift({ padding: 10 })],
    // Poll each frame so the menu tracks the cursor instead of detaching on scroll.
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, { animationFrame: true }),
  });

  // Set virtual reference from cursor position
  useEffect(() => {
    if (!getReferenceClientRect) {
      setIsReady(false);
      return;
    }

    const initialRect = getReferenceClientRect();
    if (!initialRect) {
      setIsReady(false);
      return;
    }

    setOriginPoint({ x: initialRect.left, y: initialRect.top });

    refs.setReference({
      getBoundingClientRect: () =>
        getReferenceClientRect() ?? new DOMRect(0, 0, 0, 0),
    });

    setIsReady(true);
  }, [getReferenceClientRect, refs]);

  // Prevent page and editor scroll when popup is visible
  useEffect(() => {
    if (!show) return;

    const originalBodyOverflow = document.body.style.overflow;
    const originalEditorOverflow = editorContainer?.style.overflow;

    document.body.style.overflow = 'hidden';
    if (editorContainer) {
      editorContainer.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      if (editorContainer && originalEditorOverflow !== undefined) {
        editorContainer.style.overflow = originalEditorOverflow;
      }
    };
  }, [show, editorContainer]);

  if (!show || !isReady || !floatingContainer) return null;

  // Calculate transform-origin based on cursor position relative to floating element
  const transformOrigin =
    originPoint && x !== null && y !== null
      ? `${originPoint.x - x}px ${originPoint.y - y}px`
      : 'top left';

  return createPortal(
    <div ref={refs.setFloating} style={floatingStyles}>
      <AnimatePresence>
        {show && (
          <motion.div
            className="pointer-events-auto"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{
              type: 'keyframes',
              ease: 'easeOut',
              duration: 0.15,
            }}
            style={{ transformOrigin }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>,
    floatingContainer
  );
}
